<script setup>
  import {computed, nextTick, onMounted, ref, watch} from "vue";

  import ComponentCategoryBadge from "@components/Badges/CategoryBadge.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentTodoListTodoItem from "@components/Lists/TodoList/TodoItem.vue";
  import {useForm} from "@inertiajs/vue3";
  import {MedButton, MedIcon} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";
  import {debounce} from "es-toolkit";
  import Sortable from "sortablejs";

  defineOptions({
    name: "ComponentTodoList",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    /**
     * layer: embedded in layer
     */
    layer: {
      type: Boolean,
      default: false,
    },
  });

  const refCounterBadge = ref(null);
  const expanded = ref(false);

  const pendingItems = ref((props.patient?.todos ?? []).filter((todo) => todo.completed_at === null));

  const refDraggable = ref(null);
  const isDragging = ref(false);

  const sorting = useForm({});
  const sortable = ref(null);
  const sortCancelToken = ref(null);

  const form = useForm({
    label: null,
  });

  onMounted(() => {
    initSortable();
  });
  const initSortable = () => {
    if (sortable.value !== null) {
      return; // already initialized
    }
    if (refDraggable.value === null) {
      return; // refDraggable is not available yet
    }
    sortable.value = Sortable.create(refDraggable.value, {
      handle: "[drag-handle]",
      draggable: "[drag-item]",
      direction: "vertical",
      ghostClass: "sortable-ghost",
      dataIdAttr: "drag-item", // requires `drag-item="{{item.id}}"`
      store: {
        /**
         * Save the order of elements. Called onEnd (when the item is dropped).
         * @param {Sortable} sortable
         */
        set: debounce((sortable) => {
          if (sortCancelToken.value) {
            sortCancelToken.value.cancel();
          }

          const newSortOrder = sortable.toArray();

          // change order in our local object immediately
          // to keep reactivity
          pendingItems.value = pendingItems.value.toSorted((a, b) => {
            return newSortOrder.indexOf(a.id) - newSortOrder.indexOf(b.id);
          });

          // save new order
          sorting
            .transform((data) => ({
              ...data,
              order: newSortOrder,
            }))
            .post(route("todos.sort", {patient: props.patient.id}), {
              preserveScroll: true,
              onCancelToken: (cancelToken) => (sortCancelToken.value = cancelToken),
            });
        }, 1000),
      },
      onStart: () => {
        isDragging.value = true;
      },
      onEnd: () => {
        isDragging.value = false;
      },
    });
  };

  const completed = computed(() => {
    return (props.patient?.todos ?? []).filter((todo) => todo.completed_at !== null).toReversed();
  });

  watch(
    () => props.patient.todos,
    (newValue) => {
      pendingItems.value = (newValue ?? []).filter((todo) => todo.completed_at === null);
    },
    {deep: true},
  );

  watch(
    () => completed.value,
    (newValue, oldValue) => {
      if (newValue.length === 1 && oldValue.length === 0) {
        if (expanded.value === false) {
          expanded.value = true;
        }
        nextTick(() => {
          refCounterBadge.value?.classList?.add("animate-bounce", "[animation-iteration-count:1!important]"); //  []");
        });
      } else {
        refCounterBadge.value?.classList?.remove("animate-bounce", "[animation-iteration-count:1!important]"); //  [animation-iteration-count:1!important]");
      }
    },
  );

  function save() {
    form.post(route("todos.store", {patient: props.patient.id}), {
      preserveScroll: true,
      onSuccess: () => {
        form.reset();
        initSortable();
      },
    });
  }
</script>

<template>
  <div>
    <h5 v-if="!layer" class="text-2xl">Offene Aufgaben</h5>

    <ul v-if="pendingItems.length > 0" ref="refDraggable" class="mt-4 space-y-2">
      <li
        v-for="(todo, index) in pendingItems"
        :key="todo.id"
        :drag-item="todo.id"
        class="flex items-baseline"
        :data-test="getTestId(`todo-${index}-open`)"
      >
        <ComponentTodoListTodoItem
          drag-handle
          class="flex-1 cursor-move"
          :layer="layer"
          :todo="todo"
          :patient-id="patient.id"
          :test-id="getTestId(`todo-${index}-open`)"
        />
      </li>
    </ul>

    <div v-else class="mt-1 flex items-center space-x-2 text-gray-500">
      <ComponentIcon>thumb_up_off_alt</ComponentIcon>
      <span>Keine offenen Aufgaben</span>
    </div>

    <form @submit.prevent="save()">
      <div class="mt-4 flex items-center space-x-4">
        <ComponentInput
          v-model="form.label"
          placeholder="Neue Aufgabe hinzufügen"
          :hide-clear="true"
          :test-id="getTestId('todo-label')"
        />

        <MedButton
          aria-label="Aufgabe hinzufügen"
          flat
          :is-loading="form.processing"
          type="submit"
          variant="primary"
          icon="add"
          size="md"
          rounded="full"
          :data-test="getTestId('add-todo-button')"
        />
      </div>
    </form>

    <template v-if="completed.length > 0">
      <button
        class="mt-8 flex w-full cursor-pointer items-center justify-between font-semibold text-gray-500 hover:text-gray-900"
        :data-test="getTestId('todo-toggle-completed')"
        @click="expanded = !expanded"
      >
        <span class="flex items-center">
          <MedIcon
            size="lg"
            icon="keyboard_arrow_down"
            :class="
              cn('transition-transform duration-200', {
                'rotate-180': expanded,
              })
            "
          />
          <span class="ml-1 text-sm">erledigte Aufgaben</span>
        </span>

        <span ref="refCounterBadge">
          <ComponentCategoryBadge :label="completed.length" color="gray" />
        </span>
      </button>

      <ul v-if="expanded" class="mt-4 space-y-2">
        <li v-for="(todo, index) in completed" :key="todo.id">
          <ComponentTodoListTodoItem
            :layer="layer"
            :todo="todo"
            :patient-id="patient.id"
            :test-id="getTestId(`todo-${index}-completed`)"
          />
        </li>
      </ul>
    </template>
  </div>
</template>
